/**
 * Display formatting for values that are stored in machine form.
 *
 * Every one of these exists because the raw stored value is correct for the database and wrong
 * for a person: money is an integer count of centavos, dates are bare ISO strings, and the API
 * speaks enum literals. Keep the conversions here — a formatter duplicated into a page is a
 * formatter that will drift away from the others within a year.
 *
 * Money is NEVER converted to a float for arithmetic. These helpers divide only at the moment of
 * display; every calculation stays in integer minor units in the backend engines.
 */

import { unitFor } from './units';

/** Currencies this business actually transacts in: lempira (local) and US dollar (tourist). */
export type CurrencyCode = 'HNL' | 'USD';

const CURRENCY_SYMBOL: Record<CurrencyCode, string> = { HNL: 'L', USD: '$' };

/**
 * Minor units → "L 16,000.00". The symbol is always shown: this bar quotes both lempira and
 * dollars on the same menu, so an unlabelled number is genuinely ambiguous to staff.
 */
export function formatMoney(
  minor: number | null | undefined,
  currency: CurrencyCode | string = 'HNL',
): string {
  if (typeof minor !== 'number' || !Number.isFinite(minor)) return '—';
  const symbol = CURRENCY_SYMBOL[currency as CurrencyCode] ?? '';
  const major = (minor / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return symbol ? `${symbol} ${major}` : major;
}

/**
 * Just the symbol, for the handful of places a formatted string will not fit — the prefix inside a
 * number box, where the box already is the amount and "L 0.00" cannot be typed over.
 */
export function currencySymbol(currency: CurrencyCode | string = 'HNL'): string {
  return CURRENCY_SYMBOL[currency as CurrencyCode] ?? '';
}

/** Compact variant for dense tables, dropping the cents on round amounts: "L 16,000". */
export function formatMoneyCompact(
  minor: number | null | undefined,
  currency: CurrencyCode | string = 'HNL',
): string {
  if (typeof minor !== 'number' || !Number.isFinite(minor)) return '—';
  if (minor % 100 !== 0) return formatMoney(minor, currency);
  const symbol = CURRENCY_SYMBOL[currency as CurrencyCode] ?? '';
  const major = (minor / 100).toLocaleString('en-US');
  return symbol ? `${symbol} ${major}` : major;
}

/** Major-unit input (what a person types) → minor units for storage. Half-up at the cent. */
export function toMinorUnits(major: number | string | null | undefined): number | undefined {
  if (major === '' || major === null || major === undefined) return undefined;
  const n = typeof major === 'string' ? Number(major) : major;
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

/** Minor units → the major-unit number a form field should show. */
export function toMajorUnits(minor: number | null | undefined): number | '' {
  return typeof minor === 'number' && Number.isFinite(minor) ? minor / 100 : '';
}

/**
 * What a Mantine `NumberInput` holds: the number, or the half-typed string on the way to it.
 *
 * `NumberInput.onChange` is declared `(value: number | string) => void`, and the string arm is not
 * the rare case it reads like. Mantine hands back the STRING for anything its own source calls an
 * in-flight value, and that test is `/\.\d*0$/` — **every amount whose last decimal digit is a
 * zero**. "12.50", "1.00", "800.00", "2.50 kg". Worse, `fixedDecimalScale` (which every money box
 * here sets, correctly) makes the box's value end in two decimals on blur, so the string arm is
 * not an edge case at all: it is what a money field hands back *every single time*.
 *
 * A handler written `typeof v === 'number' ? v : ''` therefore throws the amount away. It fails
 * silently and it fails invisibly — the box goes on showing "L 12.50", because Mantine keeps its
 * own display state, while the form holds nothing and answers "say how much". Measured on 2026-07-28
 * in a browser: a part payment, a credit note, a bill and a vendor price were all unrecordable, and
 * every one of them looked filled in.
 *
 * So state keeps **whatever the box handed back**, string and all, and the parse happens at the one
 * place the value is used — `toMinorUnits` for money, `typedNumber` for a count. Round-tripping the
 * string is what lets somebody type "12." and then "5": a handler that stored 12 there would feed
 * `12` back as the box's value mid-keystroke and eat the separator.
 */
export type NumberInputValue = number | string;

/**
 * A `NumberInput`'s value → the number it means, or `undefined` for empty and unparseable.
 *
 * The counterpart of `toMinorUnits` for quantities, which are not money and are not scaled.
 */
export function typedNumber(value: NumberInputValue | null | undefined): number | undefined {
  if (value === null || value === undefined) return undefined;
  const n = typeof value === 'string' ? Number(value.trim()) : value;
  if (typeof value === 'string' && value.trim() === '') return undefined;
  return Number.isFinite(n) ? n : undefined;
}

/**
 * A counted quantity and what it is counted in: "13.5 kg", "8 bottles", "2.5".
 *
 * Every screen that shows one is showing something that has been added up, and a sum of decimals
 * is 3.4000000000000004 as readily as 3.4 — a manager who reads that once stops believing the
 * column. Rounded to a thousandth: finer than anything the bar counts, coarse enough that no
 * arithmetic noise reaches the screen, and trailing zeros dropped so a whole case still reads
 * "8 bottles" rather than "8.000 bottles".
 *
 * **The unit is pluralised, and never by a rule.** It used to print exactly what the owner typed,
 * so a short delivery read "3 bottle short of the 10 bottle ordered". Adding an "s" would fix that
 * sentence and break "2 kg" into "2 kgs" — the unit is free text and the measures do not change —
 * so the platform asks for both forms the first time a unit is used and `lib/units.ts` answers
 * from that. A unit nobody has answered for prints its singular, exactly as it did before.
 *
 * One home, for the reason every formatter in this file has one home: four of these were written
 * in a single week — on an order, on a delivery, on a stock item and on the running-low list — and
 * the one that skipped the rounding was the one showing a sum.
 */
export function formatQty(qty: number | null | undefined, unit?: string | null): string {
  if (typeof qty !== 'number' || !Number.isFinite(qty)) return '—';
  const rounded = Math.round(qty * 1000) / 1000;
  return unit ? `${rounded} ${unitFor(rounded, unit)}` : String(rounded);
}

/**
 * Millionths of a stock unit → the quantity a person reads: `10_000` on a case-counted rum reads
 * "0.01 case".
 *
 * The pour report and "what can I make" carry every quantity as INTEGER MILLIONTHS, because that is
 * the grid the depletion and the cost roll-up share and 45 mL out of a case of twelve 750 mL
 * bottles is five thousandths of a case — a figure that does not survive being passed around as a
 * decimal. Turning it back into something readable happens here and nowhere else, for exactly the
 * reason dividing money by 100 happens here and nowhere else: a screen that does its own division
 * is a screen that will one day divide by the wrong number and look entirely reasonable doing it.
 */
export function formatMicroQty(micro: number | null | undefined, unit?: string | null): string {
  if (typeof micro !== 'number' || !Number.isFinite(micro)) return '—';
  return formatQty(micro / 1_000_000, unit);
}

/**
 * Basis points → "15%". Rates are stored as integer bp so that 8.25% survives a round trip;
 * trailing zeros are trimmed so the common whole-percent case reads cleanly.
 */
export function formatRateBp(bp: number | null | undefined): string {
  if (typeof bp !== 'number' || !Number.isFinite(bp)) return '—';
  const pct = bp / 100;
  return `${Number(pct.toFixed(2))}%`;
}

/**
 * FX micro-units → "26.7". The system stores 26_700_000 for 26.7 L/$ so that a rate change is
 * exact rather than a float approximation.
 */
export function formatFxRate(micros: number | null | undefined): string {
  if (typeof micros !== 'number' || !Number.isFinite(micros)) return '—';
  return String(Number((micros / 1_000_000).toFixed(6)));
}

const DATE_FORMATS = new Map<string, Intl.DateTimeFormat>();
const DATE_TIME_FORMATS = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(locale: string): Intl.DateTimeFormat {
  const present = DATE_FORMATS.get(locale);
  if (present) return present;
  const created = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  DATE_FORMATS.set(locale, created);
  return created;
}

function dateTimeFormatter(locale: string): Intl.DateTimeFormat {
  const present = DATE_TIME_FORMATS.get(locale);
  if (present) return present;
  const created = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  DATE_TIME_FORMATS.set(locale, created);
  return created;
}

/**
 * "2026-07-24" → "Jul 24, 2026".
 *
 * Bare calendar dates are parsed as local noon rather than handed to `new Date(str)`, which
 * treats a date-only string as UTC midnight — that renders as the *previous day* everywhere west
 * of Greenwich, which is to say everywhere in Honduras. Getting a hire date or an effective date
 * off by one is exactly the kind of quiet wrongness that erodes trust in the whole system.
 */
export function formatDate(value: string | null | undefined, locale = 'en-US'): string {
  const d = parseLoose(value);
  return d ? dateFormatter(locale).format(d) : '—';
}

/**
 * "2026-06" → "June 2026".
 *
 * A whole month is a real unit in this business — it is what an accountant files — and it was
 * reaching the Activity log as the stored `2026-06`. The name is spelled in full rather than
 * shortened, because a month standing on its own has room for it and "June 2026" is how somebody
 * says it out loud. Parsed as the first of the month at local noon, for the reason above.
 */
const MONTH_FMT = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long' });
export function formatMonth(value: string | null | undefined): string {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return formatDate(value);
  const d = parseLoose(`${value}-01`);
  return d ? MONTH_FMT.format(d) : '—';
}

/** "2026-07-24T18:30:00Z" → "Jul 24, 2026, 12:30 PM" in the viewer's zone. */
export function formatDateTime(value: string | null | undefined, locale = 'en-US'): string {
  const d = parseLoose(value);
  return d ? dateTimeFormatter(locale).format(d) : '—';
}

/** "3 days ago" / "in 2 months" — pairs with an absolute date, never replaces it. */
export function formatRelative(value: string | null | undefined): string {
  const d = parseLoose(value);
  if (!d) return '—';
  const diffMs = d.getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000_000],
    ['month', 2_592_000_000],
    ['week', 604_800_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];
  for (const [unit, ms] of units) {
    if (Math.abs(diffMs) >= ms) return rtf.format(Math.round(diffMs / ms), unit);
  }
  return 'just now';
}

/** How long someone has been employed: "2 years, 3 months". Null until we know the start. */
export function formatDuration(fromIso: string | null | undefined, toIso?: string | null): string | null {
  const from = parseLoose(fromIso);
  if (!from) return null;
  const to = parseLoose(toIso) ?? new Date();
  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) months -= 1;
  if (months < 0) return null;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts: string[] = [];
  if (years) parts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (rem) parts.push(`${rem} month${rem === 1 ? '' : 's'}`);
  return parts.length ? parts.join(', ') : 'less than a month';
}

/** Date → the "YYYY-MM-DD" the API stores, using local calendar parts (never toISOString). */
export function toIsoDate(d: Date | null | undefined): string | undefined {
  if (!d || Number.isNaN(d.getTime())) return undefined;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "YYYY-MM-DD" or ISO timestamp → Date, for seeding a date picker. */
export function fromIsoDate(value: string | null | undefined): Date | null {
  return parseLoose(value);
}

/**
 * Which trading day it is right now at the bar, in the bar's own timezone.
 *
 * Two things make this not simply "today". The viewer may be anywhere — these screens get read
 * from another country often enough that the browser's calendar is not the bar's. And the trading
 * day rolls at 04:00 local rather than at midnight, so a bill rung at 01:30 belongs to the night
 * before: a screen defaulting to the calendar date shows an empty list at 2 a.m. while service is
 * still going on.
 */
/** How far the given zone is from UTC at that instant, in milliseconds. */
function zoneOffsetMs(timezone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const hour = String(Number(get('hour')) % 24).padStart(2, '0');
  const asIfUtc = Date.parse(
    `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}Z`,
  );
  return asIfUtc - at.getTime();
}

/**
 * A calendar day at the bar, as the two instants that bound it.
 *
 * Every "what happened on the 14th" filter sends timestamps, and the day it means is the bar's day,
 * not the reader's. Built off the browser's own midnight, "the 14th" viewed from Madrid starts six
 * hours early and ends six hours early — it drops the whole evening of the 14th, which at a bar is
 * the day.
 */
export function zonedDayRange(date: string, timezone: string): { from: string; to: string } {
  const naiveStart = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(naiveStart)) return { from: date, to: date };
  const offset = zoneOffsetMs(timezone, new Date(naiveStart));
  const start = naiveStart - offset;
  return { from: new Date(start).toISOString(), to: new Date(start + 86_400_000 - 1).toISOString() };
}

export function businessDateIn(timezone: string, cutoffMinutes = 240, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const date = `${get('year')}-${get('month')}-${get('day')}`;
  const minutes = (Number(get('hour')) % 24) * 60 + Number(get('minute'));
  if (minutes >= cutoffMinutes) return date;
  const previous = new Date(`${date}T12:00:00Z`);
  previous.setUTCDate(previous.getUTCDate() - 1);
  return previous.toISOString().slice(0, 10);
}

/**
 * THE DATE SOMEBODY AT THE BAR WOULD WRITE ON A PIECE OF PAPER RIGHT NOW.
 *
 * Wall-clock on the island, not the 04:00 trading day and emphatically not the browser's calendar.
 * Nothing is invoiced at two in the morning, so a bill, a credit slip or an effective-from date
 * wants the plain local day — but it wants the BAR's local day. Roatán is UTC−6, so from six in the
 * evening `new Date().toISOString().slice(0, 10)` answers tomorrow, and a bill typed at 19:30 on
 * the last night of July was dated 1 August and fell out of the July return. That defect has now
 * been found six times in this repository, on both sides of the wire.
 *
 * The same function as the trading day with the roll set to midnight, so there is one piece of
 * timezone arithmetic here rather than two that drift.
 */
export function todayIn(timezone: string): string {
  return businessDateIn(timezone, 0);
}

function parseLoose(value: string | null | undefined): Date | null {
  if (!value) return null;
  // Date-only strings get an explicit local noon so timezone maths can never shift the day.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = dateOnly ? new Date(`${value}T12:00:00`) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Integer minutes → "7h", "7h 30m". Durations are stored as whole minutes everywhere in this
 * system, and every screen that shows one — schedules, timesheets, the pay register — needs the
 * same reading. It lived in three files before this, which is how "7h 30m" and "7.5h" end up on
 * two screens describing the same shift.
 *
 * Zero is an answer, not a blank. It used to print the same dash as "we have no figure", so a day
 * off read "scheduled —" beside a "Worked on a day off" flag: the one row where the manager needs
 * to know the roster said nothing, and the screen said it did not know. Four screens had each
 * patched around this on their own, in four different wordings, and every screen that had not
 * noticed was still wrong. A dash now means only that no figure reached us; a real zero says so.
 */
export function formatHours(minutes: number | null | undefined): string {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes < 0) return '—';
  if (minutes === 0) return '0h';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h ${String(rest).padStart(2, '0')}m`;
}
